/**
 * Utilitaires pour la gestion d'erreurs
 */

/**
 * Wrapper pour les opérations async qui peuvent échouer
 * @param {Promise} promise - La promesse à exécuter
 * @param {string} context - Contexte pour le logging
 * @returns {Promise<[Error|null, any]>} - [error, result]
 */
export function safeAsync(promise, context = '') {
  return promise
    .then(result => [null, result])
    .catch(error => {
      console.error(`[${context}] Erreur:`, error);
      return [error, null];
    });
}

/**
 * Wrapper pour les opérations JSON.parse
 * @param {string} jsonString - String JSON à parser
 * @param {any} fallback - Valeur de fallback si le parsing échoue
 * @param {string} context - Contexte pour le logging
 * @returns {any} - Objet parsé ou fallback
 */
export function safeJsonParse(jsonString, fallback = null, context = '') {
  if (jsonString === null || jsonString === undefined || jsonString.constructor !== String) {
    return jsonString; // Déjà un objet ou valeur primitive
  }
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error(`[${context || 'JSON parsing'}] Erreur:`, error);
    return fallback;
  }
}

/**
 * Wrapper pour les opérations qui peuvent lever des exceptions
 * @param {Function} fn - Fonction à exécuter
 * @param {any} fallback - Valeur de fallback
 * @param {string} context - Contexte pour le logging
 * @returns {Promise<any>} - Promise avec résultat ou fallback
 */
export function safeTry(fn, fallback = null, context = '') {
  return Promise.resolve()
    .then(() => fn())
    .catch(error => {
      console.error(`[${context || 'safeTry'}] Erreur:`, error)
      return fallback
    })
}

/**
 * Chaîne les opérations avec gestion d'erreur automatique
 * @param {Array<Function>} operations - Array de fonctions à chaîner
 * @param {string} context - Contexte pour le logging
 * @returns {any} - Résultat de la dernière opération ou null
 */
export function chainSafe(operations, context = '') {
  return operations.reduce(async (resultPromise, operation) => {
    const result = await resultPromise;
    const [error, newResult] = await safeAsync(
      Promise.resolve().then(() => operation(result)),
      context || 'Chain operation'
    );
    return error ? null : newResult;
  }, Promise.resolve(null));
}

/**
 * Valide et transforme les données avec fallback
 * @param {any} data - Données à valider
 * @param {Function} validator - Fonction de validation
 * @param {Function} transformer - Fonction de transformation
 * @param {any} fallback - Valeur de fallback
 * @returns {any} - Données transformées ou fallback
 */
export function validateAndTransform(data, validator, transformer, fallback = null) {
  if (!validator(data)) {
    return fallback;
  }
  
  const [error, result] = safeAsync(
    Promise.resolve().then(() => transformer(data)),
    'Data transformation'
  );
  
  return error ? fallback : result;
}

/**
 * Utilitaires de vérification de type sans typeof
 */
export const TypeUtils = {
  isString: (value) => value !== null && value !== undefined && value.constructor === String,
  isNumber: (value) => value !== null && value !== undefined && value.constructor === Number && !Number.isNaN(value),
  isBoolean: (value) => value !== null && value !== undefined && value.constructor === Boolean,
  isArray: (value) => Array.isArray(value),
  isObject: (value) => value !== null && value !== undefined && value.constructor === Object,
  isFunction: (value) => value instanceof Function,
  isNull: (value) => value === null,
  isUndefined: (value) => value === undefined,
  isNullOrUndefined: (value) => value === null || value === undefined,
  isPlainObject: (value) => value !== null && value !== undefined && 
    (value.constructor === Object || Object.prototype.toString.call(value) === '[object Object]')
};
